import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useDate, useMoney, useT } from '../i18n/index.js';
import { useSession } from '../store/session.js';
import { Badge, Button, Card, Empty, Spinner } from '../components/ui.js';

/**
 * MIS PEDIDOS — destino del deep-link desde Besbello (`{PRENDAS_URL}/mis-pedidos`).
 *
 * Si el visitante llega sin sesion, se le manda directo al SSO con `next` a esta
 * misma ruta: el enlace desde la app de Besbello tiene que abrir SUS pedidos,
 * no una pantalla de login sin contexto.
 */
export function OrdersPage() {
  const t = useT();
  const money = useMoney();
  const formatDate = useDate();
  const { user, login } = useSession();
  const [items, setItems] = useState<Array<{ id: string; reference: string; status: string; total: { cents: number }; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      void login('/mis-pedidos');
      return;
    }
    api
      .listOrders()
      .then((response) => setItems(response.items as never))
      .finally(() => setLoading(false));
  }, [user, login]);

  if (!user || loading) return <Spinner label={t('app.loading')} />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 text-2xl font-semibold">{t('orders.title')}</h1>

      {items.length === 0 ? (
        <Empty title={t('orders.empty')}>
          <Link to="/">
            <Button variant="secondary" className="mt-3">
              {t('nav.catalog')}
            </Button>
          </Link>
        </Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((order) => (
            <li key={order.id}>
              <Link to={`/pedidos/${order.id}`}>
                <Card className="flex items-center gap-3 transition-shadow hover:shadow-md">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{order.reference}</p>
                    <p className="text-xs text-tinta-suave">{formatDate(order.createdAt)}</p>
                  </div>
                  <Badge tone={statusTone(order.status)}>{t(`orders.statuses.${order.status}`)}</Badge>
                  <span className="tabular-nums font-semibold">{money(order.total.cents)}</span>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function statusTone(status: string): 'neutral' | 'ok' | 'warning' | 'error' {
  if (status === 'DELIVERED' || status === 'READY' || status === 'PAID') return 'ok';
  if (status === 'CANCELLED' || status === 'REFUNDED') return 'error';
  if (status === 'AWAITING_PAYMENT' || status === 'QUOTE_REQUESTED') return 'warning';
  return 'neutral';
}
